import type { ReactNode } from 'react';
import { noticeOf, shownSince, useConnectivity, type NoticeKind } from '../api/connectivity';
import { ca } from '../i18n/ca';
import { isConnectionProblem, retryConnection, whenText } from './connectionHelpers';

/**
 * The three states every page that reads from the platform can be in besides
 * "here it is" (roadmap item pagines-que-diuen-que-carreguen, UI doc 7):
 *
 * - loading: `PageLoading`, the shapes of what is coming, never a blank page;
 * - old or no connection: `ConnectionNotice`, a band above the last known
 *   state (never a spinner over it) saying since when it is known and that
 *   nothing is sent meanwhile; the shell draws it on every page from
 *   `connectivity.ts`, the session page with its own reading of the stream;
 * - nothing arrived: `PageProblem`, a sentence and a way to ask again, never
 *   a raw error.
 */

/**
 * The band that says the page is offline, cannot reach the platform, or that
 * the connection is back (with part of the page a stored copy, or not). `hint` is the page's own
 * sentence on what it does meanwhile (the session page tries again by itself).
 */
export function ConnectionNotice({
  kind,
  since,
  hint,
  onRetry = retryConnection,
}: {
  kind: NoticeKind;
  since: number | null;
  hint?: ReactNode;
  onRetry?: () => void;
}) {
  if (kind === 'none') return null;
  const t = ca.connection;
  if (kind === 'stored' || kind === 'back') {
    return (
      <section className="band offline stored" role="status" aria-label={t.stored}>
        <i className="bi bi-arrow-repeat" aria-hidden="true" />
        <strong className="band-title">{t.stored}</strong>
        <span className="grow muted small">
          {kind === 'back' ? t.backHint : t.storedHint(since === null ? '' : whenText(since))}
        </span>
        <button type="button" onClick={() => window.location.reload()}>
          <i className="bi bi-arrow-clockwise" aria-hidden="true" /> {t.refresh}
        </button>
      </section>
    );
  }
  const title = kind === 'offline' ? t.offline : t.unreachable;
  return (
    <section className="band offline" role="alert" aria-label={title}>
      <i className="bi bi-wifi-off" aria-hidden="true" />
      <strong className="band-title">{title}</strong>
      <span className="grow muted small">
        {since === null ? t.sinceUnknown : t.since(whenText(since))} {hint ? <>{hint} </> : null}
        {t.noWrites}
      </span>
      <button type="button" onClick={onRetry}>
        <i className="bi bi-arrow-clockwise" aria-hidden="true" /> {t.retry}
      </button>
    </section>
  );
}

/** The shell's notice: every page's, from what `apiFetch` heard last. */
export function ShellConnectionNotice() {
  const c = useConnectivity();
  if (c.ownNotices > 0) return null;
  return <ConnectionNotice kind={noticeOf(c)} since={shownSince(c)} />;
}

/**
 * No connection and nobody signed in, on any page (the sign-in page
 * included): the device keeps no copy of anybody's data then, and when the
 * account signed out here it says that sign-out is why (web card 227,
 * 2026-10-19: after «Surt» and airplane mode nothing said either).
 */
export function SignedOutOfflineNotice({
  kind,
  signedOut,
  onRetry = retryConnection,
}: {
  kind: 'offline' | 'unreachable';
  signedOut: boolean;
  onRetry?: () => void;
}) {
  const t = ca.connection;
  const title = kind === 'offline' ? t.offline : t.unreachable;
  return (
    <section className="band offline signed-out-offline" role="alert" aria-label={title}>
      <i className="bi bi-wifi-off" aria-hidden="true" />
      <strong className="band-title">{title}</strong>
      <span className="grow muted small">{signedOut ? t.signedOut : t.signedOutNever}</span>
      <button type="button" onClick={onRetry}>
        <i className="bi bi-arrow-clockwise" aria-hidden="true" /> {t.retry}
      </button>
    </section>
  );
}

/** The first paint while a page reads (UI doc 7): the shapes of cards, not a spinner. */
export function PageLoading({
  label,
  cards = 3,
  quiet = false,
}: {
  label?: string;
  cards?: number;
  /** No live region: the account's first read, gone before any page has said anything. */
  quiet?: boolean;
}) {
  const text = label ?? ca.page.loading;
  return (
    <div className="cards skeleton" aria-busy="true" aria-label={text}>
      <p className="sr-only" role={quiet ? undefined : 'status'}>
        {text}
      </p>
      {Array.from({ length: cards }, (_, i) => (
        <div key={i} className="card skeleton-card" aria-hidden="true">
          <span className="bone wide" />
          <span className="bone" />
          <span className="bone short" />
        </div>
      ))}
    </div>
  );
}

/**
 * A page whose read did not arrive: with no connection and no stored copy it
 * says so (and that one visit with a connection is enough next time);
 * otherwise the page's own sentence for the answer, or a plain one.
 */
export function PageProblem({
  error,
  text,
  onRetry,
}: {
  error: unknown;
  /** The page's sentence for an answer the platform gave (a 404's, a 403's). */
  text?: string | null;
  onRetry?: () => void;
}) {
  const offline = isConnectionProblem(error);
  return (
    <p className={offline ? 'page-problem warn' : 'page-problem error'} role="alert">
      <i
        className={`bi ${offline ? 'bi-wifi-off' : 'bi-exclamation-triangle'}`}
        aria-hidden="true"
      />{' '}
      {offline ? ca.page.offlineNothing : (text ?? ca.page.failed)}
      {onRetry && (
        <>
          {' '}
          <button type="button" className="link" onClick={onRetry}>
            {ca.page.retry}
          </button>
        </>
      )}
    </p>
  );
}
